// Taschenlampe — schmaler Spot aus der Blickrichtung.
//
// Warum eigene Datei: PlayerSystem ist schon groß, und ein anderer Agent
// arbeitet parallel an Kollision dort. Die Lampe ist reines Licht-Sync.
//
// Design (Konzept §11.1 / §7 / §11):
//   Unbegrenzt, keine Batterie-Wartungsschleife. Kosten = Sichtbarkeit:
//   im belebten Hof verrät das Licht dich — abschalten ist die vernünftige
//   Entscheidung, ohne Inventar-Hausarbeit. Multiplayer-Verrat folgt später
//   (fremde Spieler sehen den Spot); hier nur die lokale Lichtquelle.
//
// Performance (§14): kein castShadow. Ein Spot-Shadow-Map wäre pro Frame
// teurer Shadow-Pass — VR-Budget dreifach, das lohnt sich für 20–30 m Kegel
// nicht. Diffuse Spot reicht für „Ausschnitt in der Dunkelheit".

import * as THREE from 'three';
import { PALETTE } from '../render/style.js';

// Schmaler Kegel: Dunkelheit drumherum spürbar machen, kein Vollmond.
// ~22° — eng genug für „Ausschnitt", weit genug dass man den Kegel liest.
const ANGLE = Math.PI / 8;
const PENUMBRA = 0.55;           // weicher Rand, kein Hartkreis
const DISTANCE = 36;             // m — Hof/Waggon, nicht kilometerweit
const DECAY = 1.2;
// three r155+ physikalische Einheiten + ACES + Nacht-Exposure:
// zu niedrig → pechschwarz; zu hoch → Vollmond. 120 ist nach Shoot-Probe
// der Kompromiss (Boden/Gleis lesbar, drumherum dunkel).
const INTENSITY_ON = 120;
const TARGET_DIST = 10;          // m vor der Linse für Spot-Target

// Wiederverwendet — nichts pro Frame allokieren.
const _look = new THREE.Vector3();

export class PlayerFlashlight {
  /**
   * @param {THREE.Scene} scene
   * @param {typeof THREE} THREE_mod
   */
  constructor(scene, THREE_mod = THREE) {
    this.on = false;
    this.scene = scene;

    // Warme Taschenlampenfarbe aus der Palette (kein freier 0x…).
    this.light = new THREE_mod.SpotLight(
      PALETTE.sunDay,
      0,
      DISTANCE,
      ANGLE,
      PENUMBRA,
      DECAY,
    );
    this.light.name = 'playerFlashlight';
    this.light.castShadow = false; // bewusst — siehe Dateikopf
    this.light.visible = false;

    this.target = new THREE_mod.Object3D();
    this.target.name = 'playerFlashlightTarget';
    this.light.target = this.target;

    scene.add(this.light);
    scene.add(this.target);
  }

  toggle() {
    this.setOn(!this.on);
    return this.on;
  }

  setOn(value) {
    this.on = !!value;
    this.light.visible = this.on;
    this.light.intensity = this.on ? INTENSITY_ON : 0;
  }

  /**
   * Licht an Augenposition + Blickrichtung koppeln (gleiche Formel wie Kamera).
   * @param {{ pos: THREE.Vector3, yaw: number, pitch: number, eyeHeight: number }} st
   */
  sync(st) {
    if (!st?.pos) return;
    const eyeH = st.eyeHeight ?? 1.6;
    const x = st.pos.x;
    const y = st.pos.y + eyeH;
    const z = st.pos.z;

    // yaw 0 → −Z, pitch positiv = hoch (wie camera.js / player.js)
    const cp = Math.cos(st.pitch);
    const sp = Math.sin(st.pitch);
    const sy = Math.sin(st.yaw);
    const cy = Math.cos(st.yaw);
    _look.set(sy * cp, sp, -cy * cp);

    // Leicht vor dem Auge, damit der Kegel die eigene Geometrie nicht schneidet.
    this.light.position.set(
      x + _look.x * 0.18,
      y + _look.y * 0.18,
      z + _look.z * 0.18,
    );
    this.target.position.set(
      x + _look.x * TARGET_DIST,
      y + _look.y * TARGET_DIST,
      z + _look.z * TARGET_DIST,
    );
    // Target-Matrix muss aktuell sein, sonst zeigt der Spot eine Frame lang falsch.
    this.light.target.updateMatrixWorld();
  }

  dispose() {
    this.scene?.remove(this.light);
    this.scene?.remove(this.target);
    this.light.dispose?.();
  }
}

export const FLASHLIGHT_DISTANCE = DISTANCE;
export const FLASHLIGHT_INTENSITY = INTENSITY_ON;
export const FLASHLIGHT_ANGLE = ANGLE;
